import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyRound, Pencil, Trash2, Lock } from 'lucide-react';
import { describeApiError } from '../../../utils/apiError';
import { isBlockedTone } from './llmKeyStatus';
import styles from '../../PlatformAdmin/PlatformAdmin.module.scss';

const COLUMN_WIDTHS = {
  provider: '1.2fr',
  key: '1fr',
  inherit: '1fr',
  fallback: '1.1fr',
  effective: '0.9fr',
  actions: '1.4fr',
};

/**
 * Provider × key grid shared by every LLM-key screen: a platform-direct company,
 * a partner's own keys, and a partner's client companies. The screens differ
 * only in which columns show and what the effective badge means, so both are
 * injected rather than branched on here.
 *
 * Props:
 *   - rows: from buildKeyRows(providers, credentials)
 *   - resolveEffective(row) → { label, tone, tooltip? }
 *   - keyNoun: 'company key' | 'partner key' — used in headings/dialogs
 *   - fallback: { mode: 'toggle'|'readonly'|'disabled'|'hidden', label, tooltip?, text?(row) }
 *   - inherit:  { mode: 'toggle'|'hidden', label, tooltip?(row) }
 *   - openaiBlockedText: warning under OpenAI when its badge is blocked
 *   - onSetKey(row, { api_key, base_url? }), onClearKey(row), onRemoveRow?(row),
 *     onToggleFallback?(row, next), onToggleInherit?(row, next) — async; the
 *     parent refreshes its data inside them. Errors are toasted here.
 *   - clearConfirmText?(row), removeConfirmText?(row), savedMessage?(row), notify
 */
const LLMKeyMatrix = ({
  rows = [],
  resolveEffective,
  keyNoun = 'company key',
  fallback = { mode: 'toggle', label: 'Platform fallback' },
  inherit = { mode: 'hidden' },
  openaiBlockedText = null,
  onSetKey,
  onClearKey,
  onRemoveRow,
  onToggleFallback,
  onToggleInherit,
  clearConfirmText,
  removeConfirmText,
  savedMessage,
  notify,
}) => {
  const [keyDialog, setKeyDialog] = useState(null);
  const [keyValue, setKeyValue] = useState('');
  const [keyBaseUrl, setKeyBaseUrl] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type, row, heading, text, action }
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const columns = useMemo(() => {
    const list = ['provider', 'key'];
    if (inherit.mode !== 'hidden') list.push('inherit');
    if (fallback.mode !== 'hidden') list.push('fallback');
    list.push('effective', 'actions');
    return list;
  }, [inherit.mode, fallback.mode]);

  const gridStyle = useMemo(
    () => ({ '--matrix-cols': columns.map((c) => COLUMN_WIDTHS[c]).join(' ') }),
    [columns]
  );

  const keyNounTitle = keyNoun.charAt(0).toUpperCase() + keyNoun.slice(1);

  const openKeyDialog = (row) => {
    setKeyDialog(row);
    setKeyValue('');
    setKeyBaseUrl('');
  };

  const closeKeyDialog = () => {
    if (savingKey) return;
    setKeyDialog(null);
    setKeyValue('');
    setKeyBaseUrl('');
  };

  const handleSaveKey = async () => {
    if (!keyDialog || !keyValue.trim()) return;
    try {
      setSavingKey(true);
      const payload = { api_key: keyValue.trim() };
      if (keyBaseUrl.trim()) payload.base_url = keyBaseUrl.trim();
      await onSetKey(keyDialog, payload);
      notify?.(savedMessage ? savedMessage(keyDialog) : `${keyDialog.name} key saved.`, 'success');
      setKeyDialog(null);
      setKeyValue('');
      setKeyBaseUrl('');
    } catch (err) {
      notify?.(describeApiError(err, 'Failed to save key.'), 'error');
    } finally {
      setSavingKey(false);
    }
  };

  const runToggle = async (fn, row, next) => {
    try {
      setTogglingId(row.providerId);
      await fn(row, next);
    } catch (err) {
      notify?.(describeApiError(err, 'Failed to update this provider.'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      setConfirmBusy(true);
      if (confirm.type === 'clear') {
        await onClearKey(confirm.row);
        notify?.(`${confirm.row.name} key cleared.`, 'info');
      } else if (confirm.type === 'remove') {
        await onRemoveRow(confirm.row);
        notify?.(`${confirm.row.name} removed.`, 'info');
      }
      setConfirm(null);
    } catch (err) {
      notify?.(describeApiError(err, 'Action failed.'), 'error');
    } finally {
      setConfirmBusy(false);
    }
  };

  const renderFallbackCell = (row) => {
    const busy = togglingId === row.providerId;
    if (fallback.mode === 'readonly') {
      const text = fallback.text ? fallback.text(row) : row.allowFallback ? 'On' : 'Off';
      return (
        <Tooltip title={fallback.tooltip || ''} arrow>
          <span className={styles.naText}>{text}</span>
        </Tooltip>
      );
    }
    if (fallback.mode === 'disabled') {
      return (
        <Tooltip title={fallback.tooltip || ''} arrow>
          <span className={styles.naText}>{fallback.text ? fallback.text(row) : 'Not used'}</span>
        </Tooltip>
      );
    }
    if (row.hasKey) return <span className={styles.naText}>n/a — has key</span>;
    const control = (
      <Switch
        size="small"
        checked={row.allowFallback}
        disabled={busy}
        onChange={(e) => runToggle(onToggleFallback, row, e.target.checked)}
      />
    );
    return fallback.tooltip ? (
      <Tooltip title={fallback.tooltip} arrow>
        <span>{control}</span>
      </Tooltip>
    ) : (
      control
    );
  };

  const renderInheritCell = (row) => {
    const tip = inherit.tooltip ? inherit.tooltip(row) : '';
    return (
      <Tooltip title={tip} arrow>
        <span>
          <Switch
            size="small"
            checked={row.inheritFromPartner}
            disabled={togglingId === row.providerId}
            onChange={(e) => runToggle(onToggleInherit, row, e.target.checked)}
          />
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      <Box className={styles.tableCard}>
        <Box className={`${styles.matrixRow} ${styles.headRow}`} style={gridStyle}>
          <span>Provider</span>
          <span>{keyNounTitle}</span>
          {columns.includes('inherit') && <span>{inherit.label || 'Use partner key'}</span>}
          {columns.includes('fallback') && <span>{fallback.label || 'Platform fallback'}</span>}
          <span>Effective</span>
          <span className={styles.actionsHead}>Actions</span>
        </Box>
        {rows.map((row) => {
          const eff = resolveEffective(row);
          const showOpenAIWarning =
            openaiBlockedText && row.slug === 'openai' && isBlockedTone(eff.tone);
          const badge = (
            <span className={`${styles.effBadge} ${styles[eff.tone] || ''}`}>{eff.label}</span>
          );
          return (
            <Box key={row.providerId} className={styles.matrixRow} style={gridStyle}>
              <span className={styles.providerName}>
                {row.name}
                {showOpenAIWarning ? (
                  <span className={styles.providerWarn}>{openaiBlockedText}</span>
                ) : (
                  row.helperText && <span className={styles.providerHint}>{row.helperText}</span>
                )}
              </span>
              <span className={styles.keyCell}>
                {row.hasKey ? (
                  <span className={styles.keyMask}>••••{row.keyLast4}</span>
                ) : (
                  <span className={styles.keyEmpty}>— none</span>
                )}
              </span>
              {columns.includes('inherit') && (
                <span className={styles.fallbackCell}>{renderInheritCell(row)}</span>
              )}
              {columns.includes('fallback') && (
                <span className={styles.fallbackCell}>{renderFallbackCell(row)}</span>
              )}
              {eff.tooltip ? (
                <Tooltip title={eff.tooltip} arrow>
                  {badge}
                </Tooltip>
              ) : (
                badge
              )}
              <Box className={styles.actionsCell}>
                <Button
                  size="small"
                  variant={row.hasKey ? 'outlined' : 'contained'}
                  className={row.hasKey ? styles.outlineBtn : styles.primaryBtn}
                  startIcon={row.hasKey ? <Pencil size={14} /> : <KeyRound size={14} />}
                  onClick={() => openKeyDialog(row)}
                >
                  {row.hasKey ? 'Edit' : 'Set key'}
                </Button>
                {row.hasKey && onClearKey && (
                  <Button
                    size="small"
                    className={styles.subtleBtn}
                    onClick={() =>
                      setConfirm({
                        type: 'clear',
                        row,
                        heading: `Clear ${row.name} key?`,
                        text: clearConfirmText
                          ? clearConfirmText(row)
                          : `The ${keyNoun} is removed but the provider row and its settings are kept.`,
                        action: 'Clear key',
                      })
                    }
                  >
                    Clear
                  </Button>
                )}
                {row.hasRow && onRemoveRow && (
                  <IconButton
                    size="small"
                    className={styles.dangerIcon}
                    title="Remove provider row"
                    onClick={() =>
                      setConfirm({
                        type: 'remove',
                        row,
                        heading: `Remove ${row.name} entirely?`,
                        text: removeConfirmText
                          ? removeConfirmText(row)
                          : 'Deletes the provider row and its settings. The provider falls back to its defaults.',
                        action: 'Remove',
                      })
                    }
                  >
                    <Trash2 size={15} />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Key editor */}
      <Dialog open={!!keyDialog} onClose={closeKeyDialog} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {keyDialog?.hasKey ? 'Edit' : 'Set'} {keyDialog?.name} {keyNoun}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="API key"
            type="password"
            fullWidth
            required
            variant="outlined"
            placeholder="sk-..."
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
            autoComplete="new-password"
            className={styles.formField}
            helperText={keyDialog?.hasKey ? 'Entering a new key replaces the existing one.' : undefined}
          />
          <TextField
            label="Base URL (optional)"
            type="url"
            fullWidth
            variant="outlined"
            placeholder="https://..."
            value={keyBaseUrl}
            onChange={(e) => setKeyBaseUrl(e.target.value)}
            autoComplete="off"
            className={styles.formField}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeKeyDialog} disabled={savingKey} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveKey} disabled={savingKey || !keyValue.trim()} className={styles.primaryBtn}>
            {savingKey ? <CircularProgress size={20} color="inherit" /> : 'Save key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear / remove confirmation */}
      <Dialog open={!!confirm} onClose={() => !confirmBusy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{confirm?.action}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm?.heading}</Typography>
          <Typography className={styles.deleteText}>{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runConfirm}
            disabled={confirmBusy}
            className={styles.dangerButton}
            startIcon={confirmBusy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirmBusy ? 'Working...' : confirm?.action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LLMKeyMatrix;
